import React, { useMemo } from "react";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import "./style.scss";
import DonutChart, { DonutItem } from "Components/charts/DonutChart";
import { genders } from "constants/campaign";
import { CREATOR_PRIMARY_COLOR, PRIMARY_COLOR } from "constants/global";

function genderDemographyLabel(key: string): string {
    const k = key.trim().length === 1 ? key.trim().toUpperCase() : key.trim();
    return (genders as Record<string, string>)[k] ?? key;
}

const CampaignAudience = () => {
    const { campaignId } = useParams();
    const campaignInsightsData = useSelector((state: RootState) => state.campaignInsights.campaigns[campaignId!]);
    const insights = campaignInsightsData?.insights ?? {};
    const demography = insights.demography ?? {};

    const donutColors = ["#2FAFB7", "#6366F1", "#F97316", "#22C55E", "#EC4899", "#F59E0B", "#94A3B8", "#0EA5E9"];

    const cityData = useMemo(() => {
        let data: { key: string, value: number }[] = [];
        for (let [key, value] of Object.entries(demography.city ?? {})) {
            data.push({
                key, value
            });
        }
        return data.sort((a, b) => b.value - a.value).slice(0, 10);
    }, [demography.city]);

    const ageGroupData = useMemo(() => {
        let data: { key: string, value: number }[] = [];
        for (let [key, value] of Object.entries(demography.age ?? {})) {
            data.push({
                key, value
            });
        }
        return data.sort((a, b) => a.key.localeCompare(b.key)).slice(0, 10);
    }, [demography.age]);

    const genderData: DonutItem[] = useMemo(() => {
        const gender = (demography as any)?.gender as Record<string, number> | undefined;
        if (!gender) return [];

        const entries = Object.entries(gender)
            .filter(([, v]) => typeof v === "number" && Number.isFinite(v) && v > 0)
            .sort((a, b) => b[1] - a[1]);

        return entries.map(([k, v], idx) => ({
            id: k,
            name: genderDemographyLabel(k),
            y: v,
            color: k === 'F' ? CREATOR_PRIMARY_COLOR : PRIMARY_COLOR,
        }));
    }, [demography]);


    const genderTotal = useMemo(() => genderData.reduce((s, d) => s + (d.y || 0), 0), [genderData]);

    return (
        <div className="campaign-audience p-4">
            <div className="row g-4">
                <div className="col-12 col-lg-4">
                    <div className="audience-panel h-100 audience-panel--empty">
                        <div className="audience-panel__header">City</div>
                        <div className="audience-panel__body">
                            {
                                (cityData.length > 0) ? (
                                    <div className="d-flex align-items-center flex-column w-100">
                                        {
                                            cityData.map(({ key, value }, i) => {
                                                return (
                                                    <div key={i} className="d-flex align-items-center flex-column mt-3 w-100">
                                                        <div className="d-flex justify-content-between w-100 mt-auto mb-1">
                                                            <span className="demographic-key">{key}</span>
                                                            <span className="demographic-value">{value}%</span>
                                                        </div>
                                                        <div className="h-8px mx-3 w-100 bg-primary-light rounded">
                                                            <div className="bg-primary rounded h-8px" role="progressbar" style={{ width: `${value}%` }} aria-valuenow={50} aria-valuemin={0} aria-valuemax={100}></div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                ) : (
                                    <div className="audience-empty">No data available</div>
                                )
                            }
                        </div>
                    </div>
                </div>
                <div className="col-12 col-lg-4">
                    <div className="audience-panel h-100">
                        <div className="audience-panel__header">Age Group</div>
                        <div className="audience-panel__body">
                            {
                                (ageGroupData.length > 0) ? (
                                    <div className="d-flex align-items-center flex-column w-100">
                                        {
                                            ageGroupData.map(({ key, value }, i) => {
                                                return (
                                                    <div key={i} className="d-flex align-items-center flex-column mt-3 w-100">
                                                        <div className="d-flex justify-content-between w-100 mt-auto mb-1">
                                                            <span className="demographic-key">{key}</span>
                                                            <span className="demographic-value">{value}%</span>
                                                        </div>
                                                        <div className="h-8px mx-3 w-100 bg-primary-light rounded">
                                                            <div className="bg-primary rounded h-8px" role="progressbar" style={{ width: `${value}%` }} aria-valuenow={50} aria-valuemin={0} aria-valuemax={100}></div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                ) : (
                                    <div className="audience-empty">No data available</div>
                                )
                            }
                        </div>
                    </div>
                </div>
                <div className="col-12 col-lg-4">
                    <div className="audience-panel h-100">
                        <div className="audience-panel__header">Gender</div>
                        <div className="audience-panel__body audience-panel__body--chart">
                            {(genderData.length > 0) ? (
                                <DonutChart
                                    title="Gender"
                                    data={genderData}
                                    showLegend
                                    tooltipRenderer={({ name, value }) => {
                                        const pct = genderTotal > 0 ? (value / genderTotal) * 100 : 0;
                                        return (
                                            <div className="chart-tooltip">
                                                <span>
                                                    {name}: {pct.toFixed(1)}%
                                                </span>
                                            </div>
                                        );
                                    }}
                                />
                            ) : (
                                <div className="audience-empty">No data available</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CampaignAudience